import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/store";
import { toast } from "sonner";
import { ChevronLeft, Save } from "lucide-react";

const FAMILIES = ["citrico", "floral", "amaderado", "oriental", "gourmand"];
const CONCENTRATIONS = ["EDP", "EDT", "Parfum", "Cologne", "EDC"];
const GENDERS = ["femenino", "masculino", "unisex"];
const SEASONS = ["primavera", "verano", "otoño", "invierno"];
const OCCASIONS = ["día", "noche", "trabajo", "eventos", "vacaciones"];

/** join array to comma-separated string safely */
const toCsv = (a) => (Array.isArray(a) ? a.join(", ") : "");
const fromCsv = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function AdminEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [product, setProduct] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!loading && (!user || !user.is_admin)) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user?.is_admin) return;
    api.get(`/admin/products/${id}`).then(({ data }) => {
      setProduct(data);
      setForm({
        name: data.name || "",
        brand: data.brand || "",
        family: data.family || "oriental",
        concentration: data.concentration || "EDP",
        gender: data.gender || "unisex",
        description: data.description || "",
        longevity: data.longevity || "",
        projection: data.projection || "",
        similar_to: data.similar_to || "",
        season: toCsv(data.season),
        occasion: toCsv(data.occasion),
        top_notes: toCsv(data.top_notes),
        heart_notes: toCsv(data.heart_notes),
        base_notes: toCsv(data.base_notes),
        badges: toCsv(data.badges),
        size_ml: data.sizes[0]?.ml ?? 100,
        size_price: data.sizes[0]?.price ?? 0,
        size_stock: data.sizes[0]?.stock ?? 0,
        rating: data.rating ?? 4.7,
        history: data.history || "",
        origin: data.origin || "",
        usage_time: data.usage_time || "",
        usage_climate: data.usage_climate || "",
        usage_occasion: data.usage_occasion || "",
      });
    }).catch(() => toast.error("Producto no encontrado"));
  }, [id, user]);

  if (loading || !user?.is_admin || !form) {
    return <div className="p-24 text-center text-muted-foreground">Cargando…</div>;
  }

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        brand: form.brand,
        family: form.family,
        concentration: form.concentration,
        gender: form.gender,
        description: form.description,
        longevity: form.longevity,
        projection: form.projection,
        similar_to: form.similar_to || null,
        season: fromCsv(form.season),
        occasion: fromCsv(form.occasion),
        top_notes: fromCsv(form.top_notes),
        heart_notes: fromCsv(form.heart_notes),
        base_notes: fromCsv(form.base_notes),
        badges: fromCsv(form.badges),
        sizes: [{
          ml: Number(form.size_ml),
          price: Number(form.size_price),
          stock: Number(form.size_stock),
        }],
        rating: Number(form.rating),
        history: form.history,
        origin: form.origin,
        usage_time: form.usage_time,
        usage_climate: form.usage_climate,
        usage_occasion: form.usage_occasion,
      };
      await api.put(`/admin/products/${id}`, payload);
      toast.success("Producto actualizado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-12" data-testid="admin-edit-page">
      <Link to="/admin" className="inline-flex items-center gap-2 font-mono-caps text-xs mb-8 hover:text-navy transition-colors" data-testid="admin-back">
        <ChevronLeft strokeWidth={1.25} className="h-4 w-4" /> Volver al catálogo
      </Link>

      <div className="flex items-start gap-8 flex-col md:flex-row">
        <aside className="w-full md:w-64 md:sticky md:top-32 flex-shrink-0">
          <div className="border border-border p-4 bg-white">
            <img src={product.images[0]} alt="" className="w-full aspect-square object-contain bg-white" />
            <div className="mt-4 font-mono-caps text-[10px] text-muted-foreground">#Item {product.item_number || "—"}</div>
            <div className="font-mono-caps text-[10px] text-muted-foreground">Lote {product.lot_number}</div>
            <div className="font-mono-caps text-[10px] text-muted-foreground">Slug {product.slug}</div>
          </div>
        </aside>

        <form onSubmit={save} className="flex-1 space-y-8" data-testid="admin-edit-form">
          <div>
            <div className="font-mono-caps text-royal">Editar producto</div>
            <h1 className="font-serif text-4xl md:text-5xl mt-2">{form.name}</h1>
          </div>

          {/* Identidad */}
          <section className="border border-border p-6 space-y-4">
            <h2 className="font-mono-caps text-xs">Identidad</h2>
            <div className="grid md:grid-cols-2 gap-4">
              <LabeledInput label="Nombre" value={form.name} onChange={(v) => set("name", v)} testid="admin-name" />
              <LabeledInput label="Marca" value={form.brand} onChange={(v) => set("brand", v)} testid="admin-brand" />
              <LabeledSelect label="Familia olfativa" value={form.family} onChange={(v) => set("family", v)} options={FAMILIES} testid="admin-family" />
              <LabeledSelect label="Concentración" value={form.concentration} onChange={(v) => set("concentration", v)} options={CONCENTRATIONS} testid="admin-concentration" />
              <LabeledSelect label="Género" value={form.gender} onChange={(v) => set("gender", v)} options={GENDERS} testid="admin-gender" />
              <LabeledInput label="Similar a (opcional)" value={form.similar_to} onChange={(v) => set("similar_to", v)} testid="admin-similar" />
            </div>
          </section>

          {/* Precio */}
          <section className="border border-border p-6 space-y-4">
            <h2 className="font-mono-caps text-xs">Precio y stock</h2>
            <div className="grid md:grid-cols-3 gap-4">
              <LabeledInput label="Tamaño (ml)" type="number" value={form.size_ml} onChange={(v) => set("size_ml", v)} testid="admin-size-ml" />
              <LabeledInput label="Precio ($)" type="number" step="0.01" value={form.size_price} onChange={(v) => set("size_price", v)} testid="admin-size-price" />
              <LabeledInput label="Stock" type="number" value={form.size_stock} onChange={(v) => set("size_stock", v)} testid="admin-size-stock" />
            </div>
          </section>

          {/* Descripción sensorial */}
          <section className="border border-border p-6 space-y-4">
            <h2 className="font-mono-caps text-xs">Descripción sensorial</h2>
            <LabeledTextarea label="Descripción" value={form.description} onChange={(v) => set("description", v)} rows={4} testid="admin-description" />
            <div className="grid md:grid-cols-2 gap-4">
              <LabeledInput label="Longevidad" value={form.longevity} onChange={(v) => set("longevity", v)} testid="admin-longevity" placeholder="8-10 horas" />
              <LabeledInput label="Proyección" value={form.projection} onChange={(v) => set("projection", v)} testid="admin-projection" placeholder="Moderada" />
              <LabeledInput label="Estaciones (coma)" value={form.season} onChange={(v) => set("season", v)} testid="admin-season" placeholder="otoño, invierno" />
              <LabeledInput label="Ocasiones (coma)" value={form.occasion} onChange={(v) => set("occasion", v)} testid="admin-occasion" placeholder="noche, eventos" />
            </div>
            <div className="text-xs text-muted-foreground">Estaciones válidas: {SEASONS.join(", ")}. Ocasiones válidas: {OCCASIONS.join(", ")}.</div>
          </section>

          {/* Pirámide olfativa */}
          <section className="border border-border p-6 space-y-4">
            <h2 className="font-mono-caps text-xs">Pirámide olfativa</h2>
            <LabeledInput label="Notas de salida (coma)" value={form.top_notes} onChange={(v) => set("top_notes", v)} testid="admin-top-notes" placeholder="Bergamota, Pimienta rosa" />
            <LabeledInput label="Notas de corazón (coma)" value={form.heart_notes} onChange={(v) => set("heart_notes", v)} testid="admin-heart-notes" placeholder="Rosa, Jazmín" />
            <LabeledInput label="Notas de fondo (coma)" value={form.base_notes} onChange={(v) => set("base_notes", v)} testid="admin-base-notes" placeholder="Ámbar, Sándalo" />
          </section>

          {/* Historia, origen & uso (IA) */}
          <section className="border border-border p-6 space-y-4">
            <h2 className="font-mono-caps text-xs">Historia, origen y recomendación de uso</h2>
            <LabeledTextarea label="Historia" value={form.history} onChange={(v) => set("history", v)} rows={3} testid="admin-history" placeholder="2-3 frases sobre la casa perfumista y el lanzamiento" />
            <div className="grid md:grid-cols-2 gap-4">
              <LabeledInput label="Origen (país)" value={form.origin} onChange={(v) => set("origin", v)} testid="admin-origin" placeholder="Francia" />
              <LabeledSelect label="Momento de uso" value={form.usage_time} onChange={(v) => set("usage_time", v)} options={["", "día", "noche", "ambas"]} testid="admin-usage-time" />
              <LabeledSelect label="Clima" value={form.usage_climate} onChange={(v) => set("usage_climate", v)} options={["", "cálido", "frío", "templado", "versátil"]} testid="admin-usage-climate" />
              <LabeledInput label="Recomendación (1 frase)" value={form.usage_occasion} onChange={(v) => set("usage_occasion", v)} testid="admin-usage-occasion" placeholder="Ideal para cenas y eventos nocturnos" />
            </div>
          </section>

          {/* Meta */}
          <section className="border border-border p-6 space-y-4">
            <h2 className="font-mono-caps text-xs">Meta</h2>
            <LabeledInput label="Badges (coma)" value={form.badges} onChange={(v) => set("badges", v)} testid="admin-badges" placeholder="Original 100%, Bestseller" />
            <LabeledInput label="Rating" type="number" step="0.1" value={form.rating} onChange={(v) => set("rating", v)} testid="admin-rating" />
          </section>

          <div className="sticky bottom-4 flex justify-end gap-3 bg-background pt-4">
            <Link to="/admin" className="border border-border px-6 py-3 font-mono-caps hover:border-navy transition-colors" data-testid="admin-cancel">
              Cancelar
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 bg-navy text-cream px-6 py-3 font-mono-caps hover:bg-cream hover:text-navy transition-colors disabled:opacity-50"
              data-testid="admin-save-btn"
            >
              <Save strokeWidth={1.25} className="h-4 w-4" />
              {saving ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function LabeledInput({ label, value, onChange, testid, type = "text", step, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted-foreground mb-2">{label}</span>
      <input
        type={type}
        step={step}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full border border-border p-3 focus:border-navy outline-none"
        data-testid={testid}
      />
    </label>
  );
}

function LabeledSelect({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted-foreground mb-2">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border border-border p-3 focus:border-navy outline-none capitalize"
        data-testid={testid}
      >
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

function LabeledTextarea({ label, value, onChange, rows = 3, testid, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs text-muted-foreground mb-2">{label}</span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full border border-border p-3 focus:border-navy outline-none resize-y"
        data-testid={testid}
      />
    </label>
  );
}
